import { Box, Container, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AreaChart } from "src/design/AreaChart";
import { BarChart } from "src/design/BarChart";
import {
  colorLandingBlue,
  colorLandingBrand,
  colorLandingBrandLine,
  colorLandingBrandStrong,
  colorLandingInk,
  colorLandingLine,
  colorLandingMuted,
  colorLandingSurface,
  colorLandingTint,
  radiusLandingSection,
} from "src/design/tokens";
import { SectionHeading } from "./components/SectionHeading";
import {
  OfferPerformanceTable,
  PerformanceRow,
} from "./components/OfferPerformanceTable";

// Illustrative monthly figures for the dashboard preview.
const STATS = [
  { labelKey: "landingPage.analyticsSection.bookings", value: "162", trend: "↗ +12%" },
  {
    labelKey: "landingPage.analyticsSection.guests",
    value: "562",
    trend: "↗ +8%",
    blue: true,
  },
  { labelKey: "landingPage.analyticsSection.fillRate", value: "76%", trend: "↗ +5%" },
  {
    labelKey: "landingPage.analyticsSection.growthRate",
    value: "+18%",
    trendKey: "landingPage.analyticsSection.vsLastMonth",
  },
];

const TREND_DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const TREND_VALUES = [28, 42, 38, 52, 68, 80, 72];

const SERVICE_HOURS = [
  { hour: "12h", bookings: 6 },
  { hour: "13h", bookings: 12 },
  { hour: "14h", bookings: 8 },
  { hour: "15h", bookings: 3 },
  { hour: "16h", bookings: 2 },
  { hour: "17h", bookings: 3 },
  { hour: "18h", bookings: 9 },
  { hour: "19h", bookings: 16 },
  { hour: "20h", bookings: 24 },
  { hour: "21h", bookings: 22 },
  { hour: "22h", bookings: 13 },
  { hour: "23h", bookings: 5 },
];

const OFFER_ROWS: PerformanceRow[] = [
  {
    label: "−20%",
    kind: "offer",
    covers: [21, 12, 19, 2, 6, null, 11],
    totalCovers: 71,
    delta: "+31 %",
  },
];

const EVENT_ROWS: PerformanceRow[] = [
  {
    label: "Live music",
    kind: "event",
    covers: [null, 9, 7, 3, 7, null, null],
    totalCovers: 26,
    delta: "+22 %",
  },
];

interface ChartCardProps {
  title: string;
  children: React.ReactNode;
}

const ChartCard = ({ title, children }: ChartCardProps) => (
  <Box
    sx={{
      backgroundColor: colorLandingSurface,
      border: `1px solid ${colorLandingLine}`,
      borderRadius: "16px",
      p: 2.5,
      height: "100%",
    }}
  >
    <Typography
      component="h4"
      sx={{
        fontSize: 16,
        fontWeight: 700,
        letterSpacing: "-0.01em",
        color: colorLandingInk,
        mb: 1.25,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

/**
 * Data section: the headline KPIs, the weekly trend and per-service-hour
 * charts, then the offer and event performance heatmaps. Charts reuse the
 * app's own chart primitives so the landing preview matches the real product.
 */
export const AnalyticsSection = () => {
  const { t } = useTranslation();

  const bookingsKey = t("landingPage.analyticsSection.bookings");

  const trendData = TREND_DAY_KEYS.map((day, index) => ({
    name: t(`landingPage.analyticsSection.days.${day}`),
    [bookingsKey]: TREND_VALUES[index],
  }));

  const hourData = SERVICE_HOURS.map((slot) => ({
    name: slot.hour,
    [bookingsKey]: slot.bookings,
  }));

  return (
    <Box
      component="section"
      id="analytiques"
      sx={{ py: { xs: 8, md: 11.5 }, borderTop: `1px solid ${colorLandingLine}` }}
    >
      <Container maxWidth="lg">
        <SectionHeading
          title={t("landingPage.analyticsSection.title")}
          subtitle={t("landingPage.analyticsSection.subtitle")}
        />

        <Box
          sx={{
            mt: 5.75,
            backgroundColor: colorLandingTint,
            border: `1px solid ${colorLandingBrandLine}`,
            borderRadius: radiusLandingSection,
            p: { xs: 2, md: 3.25 },
          }}
        >
          <Grid container spacing={2}>
            {STATS.map((stat) => (
              <Grid item xs={6} md={3} key={stat.labelKey}>
                <Box
                  sx={{
                    backgroundColor: colorLandingSurface,
                    border: `1px solid ${colorLandingLine}`,
                    borderRadius: "16px",
                    p: 2.75,
                    height: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: 26, md: 34 },
                      letterSpacing: "-0.02em",
                      color: colorLandingInk,
                      lineHeight: 1.1,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography sx={{ color: colorLandingMuted, fontSize: 14, mt: 0.5 }}>
                    {t(stat.labelKey)}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1.5,
                      fontWeight: 700,
                      fontSize: 13,
                      color: stat.blue ? colorLandingBlue : colorLandingBrandStrong,
                    }}
                  >
                    {stat.trendKey ? t(stat.trendKey) : stat.trend}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <ChartCard title={t("landingPage.analyticsSection.bookingsTrend")}>
                <AreaChart
                  data={trendData}
                  dataKeys={[bookingsKey]}
                  palette={[colorLandingBlue]}
                  compact
                />
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <ChartCard title={t("landingPage.analyticsSection.bookingsByHour")}>
                <BarChart
                  data={hourData}
                  dataKeys={[bookingsKey]}
                  palette={[colorLandingBrand]}
                  yAxis
                />
              </ChartCard>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            mt: 3,
            backgroundColor: colorLandingTint,
            border: `1px solid ${colorLandingBrandLine}`,
            borderRadius: radiusLandingSection,
            p: { xs: 2, md: 3.25 },
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          <OfferPerformanceTable
            title={t("landingPage.analyticsSection.offersPerformance")}
            rows={OFFER_ROWS}
          />
          <OfferPerformanceTable
            title={t("landingPage.analyticsSection.eventsPerformance")}
            rows={EVENT_ROWS}
          />
        </Box>
      </Container>
    </Box>
  );
};
